import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { DateTime } from 'luxon';

const KYC_IMAGE = "https://www.verify.degencoinflip.com/static/media/KYCGuyBG.31be9a4473054fb8b0f9.png";

// Environment variables with defaults
const ALLOW_DISMISS: boolean = process.env.REACT_APP_ALLOW_DISMISS === 'true';
const DISMISS_DELAY_MS: number = ALLOW_DISMISS ? (Number(process.env.REACT_APP_VERIFICATION_DISMISS_DELAY_MS) || 120000) : 0;

interface WalletVerificationModalProps {
    show: boolean;
    onClose: () => void;
    onDismiss: () => void;
    styleCss: any;
    walletId: string;
    canDismiss: boolean;
    threshold: number;
}

export const WalletVerificationModal: React.FC<WalletVerificationModalProps> = ({
    show,
    onClose,
    onDismiss,
    styleCss,
    walletId,
    canDismiss,
    threshold
}) => {
    const [timeRemaining, setTimeRemaining] = useState<number>(DISMISS_DELAY_MS);
    const [canDismissAfterDelay, setCanDismissAfterDelay] = useState<boolean>(false);

    // If ALLOW_DISMISS is true, we'll show timer regardless of canDismiss
    // If ALLOW_DISMISS is false, we'll respect the canDismiss prop
    const shouldShowDismissButton = ALLOW_DISMISS || canDismiss;

    useEffect(() => {
        if (!show || !ALLOW_DISMISS) return;

        const startTime = DateTime.utc();
        const timer = setInterval(() => {
            const elapsed = DateTime.utc().diff(startTime).toMillis();
            const remaining = Math.max(0, DISMISS_DELAY_MS - elapsed);
            setTimeRemaining(remaining);

            if (remaining === 0) {
                setCanDismissAfterDelay(true);
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [show]);

    const formatTimeRemaining = (): string => {
        const seconds = Math.ceil(timeRemaining / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // If ALLOW_DISMISS is true, wait for timer
    // If ALLOW_DISMISS is false, use canDismiss directly
    const isDismissEnabled = ALLOW_DISMISS ? canDismissAfterDelay : canDismiss;

    const handleVerifyClick = (): void => {
        window.open("https://verify.degencoinflip.com", '_blank');
    };

    const handleDismissClick = (): void => {
        onDismiss();
    };

    const handleModalClose = (): void => {
        if (isDismissEnabled) {
            onDismiss();
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            aria-labelledby="contained-modal-title-vcenter"
            contentClassName={styleCss + `-color`}
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Body className="p-0">
                <div className="card card-user shadow-lg">
                    <div className={`card-body p-4 ${styleCss === "dark" ? " pb-0" : ""}`}>
                        <h5 className="font-weight-bold mb-3">
                            Wallet Verification Required
                        </h5>
                        <img
                            className="w-100 mb-4 rounded-3"
                            src={KYC_IMAGE}
                            alt="KYC Verification"
                            style={{ maxHeight: '200px', objectFit: 'cover' }}
                        />
                        <p className={`mb-3 ${styleCss === "dark" ? "text-light" : "text-dark"}`}>
                            Our platform is licensed and regulated by the Government of Anjouan.
                            You've reached {threshold} SOL on wallet {walletId.slice(0, 4)}...{walletId.slice(-4)}, so verification is required.
                        </p>
                        <div className={`${styleCss === "dark" ? "text-light" : "text-dark"}`}>
                            <p className="mb-2">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                Quick: Most users complete in under 2 minutes
                            </p>
                            <p className="mb-2">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                One-time: Verify once, then freely add other wallets
                            </p>
                            <p className="mb-0">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                Secure & confidential: your data is protected
                            </p>
                        </div>
                    </div>
                    <div className="card-footer text-center">
                        <button
                            className="btn btn-block w-100 btn-lg my-2 rounded-sm btn-warning"
                            onClick={handleVerifyClick}
                        >
                            VERIFY WALLET
                        </button>
                        {shouldShowDismissButton && (
                            <>
                                <button
                                    className="btn btn-block w-100 btn-lg mb-2 rounded-sm btn-secondary"
                                    onClick={handleDismissClick}
                                    disabled={!isDismissEnabled}
                                >
                                    {ALLOW_DISMISS
                                        ? (canDismissAfterDelay ? 'DISMISS' : `DISMISS (${formatTimeRemaining()})`)
                                        : 'DISMISS'
                                    }
                                </button>
                                <p className="text-muted small mb-0">
                                    Verification will soon be mandatory.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}; 
